/**
 * Per-book support helpers for the no-extension deeplink path.
 *
 * `bookSupportsDeeplink(key)` decides whether to render a
 * single-leg "Add to slip" CTA. `bookSupportsParlayDeeplink(key)`
 * additionally gates the multi-leg "Add full parlay" CTA — only
 * BetMGM and DraftKings (best-effort same-event SGP) qualify in
 * v1; everywhere else the parlay button hides and we surface a
 * one-line note explaining why.
 *
 * Keep this list aligned with the profile registry in
 * {@link ./buildBookDeeplink.ts}.
 */

/**
 * Display-key → canonical-key aliases. Different backend endpoints emit
 * the same physical sportsbook under different keys:
 *
 *   • {@code /api/v1/positive-ev/v2}  — emits raw and regional variants
 *     ({@code hardrockbet}, {@code hardrockbet_az}, {@code hardrockbet_oh}, …).
 *   • {@code /v1/odds/arbs}           — runs every leg through
 *     {@code PlayerPropsService#normalizeBookie} which collapses to a
 *     frontend display key ({@code hardrockbet} → {@code hard_rock}).
 *
 * Without this map, the arb chart's bookie key {@code hard_rock} fails
 * {@link bookSupportsDeeplink} (which only knows {@code hardrockbet}),
 * so the "Add to slip" CTA silently disappears for ~10% of arb legs
 * even though the row carries a populated {@code outcomeLink}. The
 * +EV chart hits the same gap on regional variants
 * ({@code hardrockbet_az}, etc.).
 *
 * Keep in sync with {@code PlayerPropsService.BOOKIE_KEY_MAP} on the
 * backend side — that's the source of the {@code hard_rock} alias.
 */
const BOOKIE_KEY_ALIASES: Record<string, string> = {
  hard_rock: "hardrockbet",
  hardrockbet_nj: "hardrockbet",
  hardrockbet_oh: "hardrockbet",
  hardrockbet_az: "hardrockbet",
  hardrockbet_fl: "hardrockbet",
};

/**
 * Resolves a bookmaker key to the canonical key used by the deeplink
 * profile registry and supported-set check. Lower-cases the input and
 * returns the alias when present, otherwise the lower-cased key.
 *
 * Always run book keys through this before doing a profile / supports
 * lookup so both the arb endpoint's display keys ({@code hard_rock})
 * and the +EV endpoint's regional variants ({@code hardrockbet_az})
 * funnel into the same profile.
 */
export function canonicalBookieKey(
  bookmakerKey: string | null | undefined
): string {
  if (!bookmakerKey) return "";
  const lower = bookmakerKey.toLowerCase();
  return BOOKIE_KEY_ALIASES[lower] ?? lower;
}

const SUPPORTED_DEEPLINK_BOOKS: ReadonlySet<string> = new Set([
  "draftkings",
  "fanduel",
  "betmgm",
  "espnbet",
  "thescore",
  "fanatics",
  "betparx",
  "hardrockbet",
  "caesars",
  "williamhill_us",
]);

/**
 * Books for which we can collapse a multi-leg parlay into a single
 * combined betslip URL. Other books only support per-leg deeplinks
 * in v1 — the UI hides the hero "Add full parlay" CTA for them.
 *
 * - BetMGM: documented `?options=a,b,c&type=combo` URL.
 * - DraftKings: best-effort same-event SGP rebuild (cross-event
 *   parlays still hide the CTA via runtime null check in the
 *   profile's buildUrl).
 */
const PARLAY_DEEPLINK_BOOKS: ReadonlySet<string> = new Set([
  "betmgm",
  "draftkings",
]);

export function bookSupportsDeeplink(
  bookmakerKey: string | null | undefined
): boolean {
  const canonical = canonicalBookieKey(bookmakerKey);
  if (!canonical) return false;
  return SUPPORTED_DEEPLINK_BOOKS.has(canonical);
}

export function bookSupportsParlayDeeplink(
  bookmakerKey: string | null | undefined
): boolean {
  const canonical = canonicalBookieKey(bookmakerKey);
  if (!canonical) return false;
  return PARLAY_DEEPLINK_BOOKS.has(canonical);
}

const BOOKIE_DISPLAY_NAMES: Record<string, string> = {
  betmgm: "BetMGM",
  fanduel: "FanDuel",
  draftkings: "DraftKings",
  espnbet: "ESPN BET",
  thescore: "theScore Bet",
  fanatics: "Fanatics",
  betparx: "BetParx",
  hardrockbet: "Hard Rock Bet",
  caesars: "Caesars",
  williamhill_us: "Caesars",
  betrivers: "BetRivers",
  ballybet: "Bally Bet",
  bet365: "bet365",
  betonlineag: "BetOnline",
  bovada: "Bovada",
  pinnacle: "Pinnacle",
  fliff: "Fliff",
  novig: "Novig",
  prophetx: "ProphetX",
  kalshi: "Kalshi",
  polymarket: "Polymarket",
};

/**
 * Friendly display name for a bookmaker key. Canonicalizes first so
 * arb-endpoint display keys ({@code hard_rock}) and EV regional
 * variants ({@code hardrockbet_az}) both render as "Hard Rock Bet".
 *
 * Falls back to the canonical key (lowercase) when no friendly name
 * is registered. Used by the deeplink CTA and by chart cells that
 * label fallback "Add to {Book} {price}" buttons.
 */
export function bookDisplayName(
  bookmakerKey: string | null | undefined
): string {
  const canonical = canonicalBookieKey(bookmakerKey);
  if (!canonical) return "";
  return BOOKIE_DISPLAY_NAMES[canonical] ?? canonical;
}
